import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { noop } from 'lodash-es';

interface DrawerStyle {
  width: string;
  backgroundColor: string;
  primaryColor: string;
}

interface FjSideDrawerProps {
  open?: boolean;
  onCloseRequest?: () => void;
  style: DrawerStyle;
  children?: React.ReactNode;
}

interface FjSideDrawerState {
  rightClearence: number;
}

// The drawer overrides the app theme via ThemeProvider with a DrawerStyle,
// so the styled-components below read DrawerStyle fields off `props.theme`.
const drawerTheme = (theme: DefaultTheme): DrawerStyle => theme as unknown as DrawerStyle;

class FjSideDrawer extends React.Component<FjSideDrawerProps, FjSideDrawerState> {
  static defaultProps = {
    open: false,
    onCloseRequest: noop,
  };

  constructor(props: FjSideDrawerProps) {
    super(props);
    this.state = {
      rightClearence: 0,
    };
  }

  getDrawerBounds(drawerSliderRef: HTMLDivElement | null): void {
    if (drawerSliderRef) {
      const bounds = drawerSliderRef.getBoundingClientRect();
      const rightClearence = window.innerWidth - bounds.left;
      if (this.state.rightClearence !== rightClearence) {
        this.setState({ rightClearence });
      }
    }
  }

  handleCloseRequest(): void {
    this.props.onCloseRequest?.();
  }

  render(): React.ReactNode {
    return (
      <ThemeProvider theme={this.props.style as unknown as DefaultTheme}>
        <DrawerAnchor>
          <DrawerSlider drawerOpen={this.props.open} ref={(ref) => this.getDrawerBounds(ref)}>
            <DrawerContainer>
              <DrawerHeader>
                <CloseButton onClick={() => this.handleCloseRequest()}>X</CloseButton>
              </DrawerHeader>
              <DrawerContent>{this.props.children}</DrawerContent>
            </DrawerContainer>
          </DrawerSlider>
          <DrawerClickableBackground
            drawerOpen={this.props.open}
            clearence={this.state.rightClearence}
            onClick={() => this.handleCloseRequest()}
          />
        </DrawerAnchor>
      </ThemeProvider>
    );
  }
}

export default FjSideDrawer;

const DrawerAnchor = styled.div`
  position: relative;
  width: 0px;
  height: 100%;
  overflow: visible;
`;

const DrawerSlider = styled.div<{ drawerOpen?: boolean }>`
  position: absolute;
  z-index: 1;
  top: 0px;
  left: 0px;
  width: ${(props) => (props.drawerOpen ? drawerTheme(props.theme).width : '0px')};
  height: 100%;
  overflow: hidden;
  background-color: transparent;
  transition: width 0.53s;
  transition-timing-function: ease;
  box-shadow: 4px 0px 24px -7px #444;
`;

const DrawerContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  width: ${(props) => drawerTheme(props.theme).width};
  height: 100%;
  background-color: ${(props) => drawerTheme(props.theme).backgroundColor};
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 60px;
  width: 100%;
  padding: 0px 20px;
`;

const CloseButton = styled.div`
  color: ${(props) => drawerTheme(props.theme).primaryColor};
  font-size: 24px;
  font-weight: 300;
  cursor: pointer;
  font-family: 'Roboto Light', 'Roboto', sans-serif;
`;

const DrawerContent = styled.div`
  padding: 0px 40px;
  height: calc(100% - 60px);
  overflow-y: auto;
`;

const DrawerClickableBackground = styled.div<{ drawerOpen?: boolean; clearence: number }>`
  display: ${(props) => (props.drawerOpen ? 'block' : 'none')};
  position: absolute;
  z-index: 0;
  top: 0px;
  left: 0px;
  height: 100%;
  width: ${(props) => props.clearence}px;
  background-color: transparent;
`;
